import Utilities from './Utilities';

export default class Relay {
  /**
   * Creates a new relay instance
   *
   * @param {Sensor} sensor - Current sensor instance
   * @param {Http} http - The http class used for communication
   */
  constructor(sensor, http) {
    this.name = 'RelayClass';
    this.sensor = sensor;
    this.http = http;
  }

  /**
   * Transmit the calculated and encrypted sensor data to the relay
   *
   * @param {String|Object} data - The measured data to transmit
   * @param {String} secret - Rsa encrypted password
   * @param {String} checksum - Checksum for the request
   * @return {Promise<*>} Resolves with response from the relay or with an empty result when an error occurred
   */
  transmitData(data, secret, checksum) {
    return new Promise((resolve) => {
      const {
        logger, state, systemConfig, basicSettings,
      } = this.sensor;
      const payload = {
        api: data,
        rsa: secret,
        cs: checksum,
        mo: 0,
        rp: basicSettings.rsaPadding,
        sm: systemConfig.secureMode === true ? 1 : 0,
        tc: state.runtimes,
      };
      // Try to send a GET request in secure mode
      if (systemConfig.secureMode === true) {
        let method = 'GET';
        // Serialize payload data to url encoded query parameters
        const queryParameters = Utilities.serializeQueryParameters(payload);
        // Create an url for the GET request with encoded query parameters
        let url = `${Utilities.getTransmissionProtocol()}//${state.globalParams.serviceDomainName}/relay.io?${queryParameters}`;
        // Check if URL length violates the maximum URL length criteria
        if (url.length > systemConfig.maxUrlLength) {
          // URL is too long fallback to POST
          method = 'POST';
          url = url.split('?').shift();
        }
        // Log some information
        logger.debug('Request URL (Relay)', url);
        logger.debug('Request method (Relay): ', method);
        logger.debug('Request payload (Relay): ', payload);
        this.http
          .sendRequest(this.sensor, 'data', url, method, payload, systemConfig.requestTimeout)
          .then(result => resolve(result));
      } else {
        // In none-secure mode fallback to POST as request method
        const method = 'POST';
        // Create url for the POST or PUT request without query parameters
        const url = `${Utilities.getTransmissionProtocol()}//${state.globalParams.serviceDomainName}/relay.io`;
        // Delete unnecessary payload members in none secure mode
        delete payload.aes;
        delete payload.rp;
        // Log some information
        logger.debug('Request URL (Relay)', url);
        logger.debug('Request method (Relay): ', method);
        logger.debug('Request payload (Relay): ', payload);
        this.http
          .sendRequest(this.sensor, 'data', url, method, payload)
          .then(result => resolve(result));
      }
    });
  }

  /**
   * Fetches a site specific sensor configuration from the relay
   *
   * @param {Object} initParameters - THe init parameters
   * @returns {Promise} Resolves with the response
   */
  fetchConfig(initParameters) {
    return new Promise((resolve) => {
      const {
        logger,
      } = this.sensor;
      const method = 'GET';
      const url = `${Utilities.getTransmissionProtocol()}//${initParameters.serviceDomainName}/${initParameters.site}-config.json`;
      logger.debug('Request configuration URL (Relay)', url);
      logger.debug('Request configuration method (Relay): ', method);
      this.http
        .sendRequest(this.sensor, 'config', url, method)
        .then(result => resolve(result));
    });
  }
}
